import { createContext, ReactNode, useState } from 'react'

export interface PageProvider {
  isTransitionForwards: boolean
  values: any
  changePage: (page: ReactNode) => void
  backPage: () => void
  onClose: () => void
  onSelect: (values: any) => void
  pageNumber: number
  shouldAnimate: boolean
}
const ModalContext = createContext<Partial<PageProvider>>({})

interface PageContainerProps {
  children: (currentPage: ReactNode, values: PageProvider) => ReactNode
  onClose: PageProvider['onClose']
  onSelect?: PageProvider['onSelect']
  values: PageProvider['values']
  wrapperComponent?: (currentPage: ReactNode, values: PageProvider) => ReactNode
}
const PageContainer = ({ children, onClose, onSelect, values, wrapperComponent }: PageContainerProps) => {
  const [pageHistory, updateHistory] = useState<ReactNode[]>([])
  const [isTransitionForwards, setIsTransitionForwards] = useState(true)
  const [isFirstMount, setIsFirstMount] = useState(true)

  const handleClose = () => {
    updateHistory([])
    onClose()
  }

  const handleSelect = (values: any) => {
    handleClose()
    onSelect && onSelect(values)
  }

  const changePage = (page: ReactNode) => {
    updateHistory([...pageHistory, page])
    setIsTransitionForwards(true)

    if (isFirstMount) setIsFirstMount(false)
  }

  const backPage = () => {
    if (pageHistory.length > 0) {
      const modifiedHistory = [...pageHistory]
      modifiedHistory.pop()
      updateHistory(modifiedHistory)
      setIsTransitionForwards(false)
    }
  }

  const getCurrentPage = () => {
    if (pageHistory.length > 0) return pageHistory.at(-1)
  }

  const providerValues: PageProvider = {
    isTransitionForwards,
    values,
    changePage,
    backPage,
    onClose: handleClose,
    onSelect: handleSelect,
    pageNumber: pageHistory.length,
    shouldAnimate: !isFirstMount
  }

  const currentPage = getCurrentPage()
  const content = children(currentPage, providerValues)

  return (
    <ModalContext.Provider value={providerValues}>
      {wrapperComponent ? wrapperComponent(content, providerValues) : content}
    </ModalContext.Provider>
  )
}

export default PageContainer
